import { CSSProperties, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import officeService from "api/officeService";
import { portalThemeVariables } from "utils/ebiPortalTheme";

import styles from "./portalPreview.module.scss";
import { FC, IEbiPortalPreviewProps } from "./typings";

// Makieta portalu petenta (układ z projektu Claude Design) w kolorze akcentu z formularza zakładki Wygląd: nagłówek z herbem
// i nawigacją, pasek wprowadzający katalogu, karta wniosku z przyciskiem i znaczniki stanu pism. Zmienne motywu idą na
// kontener makiety, więc działają w niej te same odcienie akcentu co w portalu (utils/ebiPortalTheme).

const EbiPortalPreview: FC<IEbiPortalPreviewProps> = ({ theme, officeName, hasEmblem, emblemVersion }) => {
  const { t } = useTranslation();
  const { data: emblemData } = officeService.useGetEmblem(emblemVersion ?? undefined, hasEmblem);
  const [emblemUrl, setEmblemUrl] = useState<string>();

  useEffect(() => {
    const blob = emblemData?.data;

    if (!blob) {
      setEmblemUrl(undefined);
      return undefined;
    }

    const url = URL.createObjectURL(blob);
    setEmblemUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [emblemData]);

  return (
    <div
      className={styles.portalPreview}
      aria-hidden="true"
      // arch-ignore inline-styl -- kolor akcentu z formularza jako zmienne CSS motywu portalu; odcienie ustawia arkusz
      style={portalThemeVariables(theme) as CSSProperties}
    >
      <div className={styles.pvHeader}>
        {hasEmblem && emblemUrl && <img className={styles.pvEmblem} src={emblemUrl} alt="" />}
        <div className={styles.pvHeaderText}>
          <div className={styles.pvBrandRow}>
            <span className={styles.pvBrand}>
              <strong>{t("ebi.layout.brand")}</strong>
              <span className={styles.pvBrandLong}>{t("ebi.title")}</span>
            </span>
            <span className={styles.pvLogin}>{t("ebi.layout.login")}</span>
          </div>
          {officeName && <div className={styles.pvOffice}>{officeName}</div>}
        </div>
      </div>

      <div className={styles.pvNav}>
        <span className={`${styles.pvNavItem} ${styles.pvNavItemActive}`}>{t("ebi.nav.catalog")}</span>
        <span className={styles.pvNavItem}>{t("ebi.nav.submissions")}</span>
        <span className={styles.pvNavItem}>
          {t("ebi.nav.parcels")}
          <span className={styles.pvBadge}>1</span>
        </span>
      </div>

      <div className={styles.pvIntro}>
        <div className={styles.pvIntroTitle}>{t("ebi.catalog.title")}</div>
        <div className={styles.pvSearch}>{t("ebi.catalog.searchPlaceholder")}</div>
      </div>

      <div className={styles.pvBody}>
        <div className={styles.pvGroup}>
          <span className={styles.pvGroupNo}>1</span>
          {t("ebi.submission.categories.general")}
        </div>
        <div className={styles.pvCard}>
          <div className={styles.pvCardTexts}>
            <span className={styles.pvCardTitle}>{t("ebiOffice.appearance.theme.sampleForm")}</span>
            <span className={styles.pvLink}>{t("ebi.catalog.details")}</span>
          </div>
          <span className={styles.pvButton}>{t("ebi.catalog.submit")}</span>
        </div>
        <div className={styles.pvPills}>
          <span className={`${styles.pvPill} ${styles.pvPillInfo}`}>{t("ebi.parcels.status.deemed")}</span>
          <span className={`${styles.pvPill} ${styles.pvPillWarning}`}>{t("ebi.parcels.status.available")}</span>
          <span className={`${styles.pvPill} ${styles.pvPillSuccess}`}>{t("ebi.parcels.status.received")}</span>
        </div>
      </div>

      <div className={styles.pvFooter}>
        <strong>{t("ebi.layout.brand")}</strong>
        {officeName && <span>· {t("ebi.layout.providedBy", { office: officeName })}</span>}
      </div>
    </div>
  );
};

export default EbiPortalPreview;
